import React, { useContext, useEffect } from "react";

import ModifierOptions from "../ModifierOptions/ModifierOptions";

import styles from "./Modifiers.module.css";
import BackArrowIcon from "~/icons/BackArrowIcon";
import ThemeContext from "~/contexts/ThemeContext";
import { ProductModalInfoTitleFontSize } from "~/theme/models/themeModels";
import { CartLineItem, OrderSource } from "~/entities/orderEntities";
import { clsx } from "clsx";

interface ModifiersProps {
  nav: string[][];
  setNav: (value: string[][]) => void;
  cart: CartLineItem;
  setCart: (cart: CartLineItem) => void;
  showError: boolean;
  orderSource: OrderSource;
}

const Modifiers: React.FC<ModifiersProps> = ({
  nav,
  setNav,
  cart,
  setCart,
  showError,
  orderSource,
}) => {
  const { template } = useContext(ThemeContext);

  const goBack = () => {
    setNav(nav.filter((_, i) => i < nav.length - 1 || i === 0));
  };

  const viewChild = (childCartIdPath: string[]) => {
    setNav([...nav, childCartIdPath]);
  };

  const cartItemByNav = (navItem: string[]): CartLineItem => {
    let curCartItem: CartLineItem | null = cart;
    navItem.forEach((childCartId) => {
      curCartItem =
        curCartItem?.children?.find((x) => x.id === childCartId) ?? null;
    });
    return curCartItem;
  };

  const curViewingCartChildPath = nav[nav.length - 1];
  const curViewingCart = cartItemByNav(curViewingCartChildPath);

  // If we somehow end up viewing a child that doesn't exist, reset navigation
  const curViewExists = !!curViewingCart;
  useEffect(() => {
    if (!curViewExists) {
      console.warn("Resetting product modal navigation");
      setNav([[]]);
    }
  }, [curViewExists, setNav]);

  const modifyCartByChildPath = (
    totalCart: CartLineItem,
    newCart: CartLineItem,
    pathToNewCart: string[],
  ): CartLineItem => {
    if (pathToNewCart.length === 0) {
      return newCart;
    }
    return {
      ...totalCart,
      children: totalCart.children.map((child) =>
        child.id !== pathToNewCart[0]
          ? child
          : modifyCartByChildPath(
              child,
              newCart,
              pathToNewCart.filter((_, i) => i !== 0),
            ),
      ),
    };
  };
  const modifyCurViewingCart = (newCart: CartLineItem) =>
    setCart(modifyCartByChildPath(cart, newCart, curViewingCartChildPath));

  // Styles
  const titleFontSize =
    ProductModalInfoTitleFontSize[template.productScreen.info.titleFontSize];
  const titleColor = template.productScreen.info.titleColour;

  return (
    curViewingCart && (
      <div className={styles.wrapper}>
        {nav.length > 1 && (
          <div
            className={clsx(styles.subOptionsWrapper, "sub-options-header")}
            style={{
              fontSize: titleFontSize,
              color: titleColor,
            }}
          >
            <div
              onClick={goBack}
              role="button"
              tabIndex={0}
              aria-label="Back"
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  goBack();
                }
              }}
            >
              <BackArrowIcon color={titleColor} />
            </div>
            <span>{curViewingCart.product.name}</span>
          </div>
        )}
        <ModifierOptions
          cart={curViewingCart}
          setCart={modifyCurViewingCart}
          viewOptionsForChild={(childCartIdPath: string[]) =>
            viewChild(curViewingCartChildPath.concat(...childCartIdPath))
          }
          showError={showError}
          orderSource={orderSource}
        />
      </div>
    )
  );
};

export default Modifiers;
